import type { FunctionComponent } from 'react';

import IconButton from '../../generic/Button/IconButton';
import Icon, { icons } from '../../generic/Icon';
import Link from '../../generic/Link/RouterLink';
import Tooltip from '../../generic/Tooltip';
import { defineSlotComponent } from '../../layouts/slots';
import type { IconClassName } from '../../generic/Icon';
import type { SlotComponent } from '../../layouts/slots';

import styles from './SettingsTableRowActions.module.less';

type BaseProps = {
  /** ID for the tooltip */
  tooltipId: string;
  /** The tooltip text */
  tooltip: string;
  /** If the link should be disabled */
  isDisabled?: boolean;
};

type LinkProps = BaseProps & {
  /** Path to for the link */
  to: string;
  /** The icon to display */
  icon: IconClassName;
};

type ViewOrEditLinkProps = BaseProps & {
  /** Path to for the link (to the view/edit page) */
  to: string;
  /** If the link should look like view or edit */
  canEdit: boolean;
};

type IconButtonProps = BaseProps & {
  /** The icon to display */
  icon: IconClassName;
  /** The onClick handler */
  onClick: () => void;
};

type SettingsTableRowActionsComponent = FunctionComponent & {
  Link: SlotComponent<LinkProps>;
  ViewOrEditLink: SlotComponent<ViewOrEditLinkProps>;
  IconButton: SlotComponent<IconButtonProps>;
};

/**
 * Generic component for building a table row action cell.
 * Usually this will contin a link to an edit/management page (see users, teams, etc.)
 *
 * Note that while this sort of follows the "slot" pattern, the component is indifferent
 * about the order of the children, which is why this component doesn't do any
 * slot selection (just renders the children in the order they come, as this is more flexible).
 */
const SettingsTableRowActions: SettingsTableRowActionsComponent = ({
  children,
}) => (
  <div className={styles.tableRowActions}>
    {children}
  </div>
);

SettingsTableRowActions.Link = defineSlotComponent<LinkProps>('Link', ({
  to,
  tooltip,
  tooltipId,
  isDisabled,
  icon,
}: LinkProps) => (
  <Tooltip
    id={tooltipId}
    tooltip={tooltip}
    placement="left"
    //  Always make the tooltip visible.
    showTooltip
  >
    <Link
      to={to}
      isDisabled={isDisabled}
      className={styles.link}
    >
      <Icon icon={icon} />
    </Link>
  </Tooltip>
));

SettingsTableRowActions.ViewOrEditLink = defineSlotComponent<ViewOrEditLinkProps>('ViewOrEditLink', ({
  to,
  tooltip,
  tooltipId,
  isDisabled,
  canEdit,
}: ViewOrEditLinkProps) => (
  <SettingsTableRowActions.Link
    to={to}
    tooltip={tooltip}
    tooltipId={tooltipId}
    isDisabled={isDisabled}
    icon={canEdit ? icons.edit : icons.eyeRegular}
  />
));

SettingsTableRowActions.IconButton = defineSlotComponent<IconButtonProps>('IconButton', ({
  icon,
  tooltip,
  tooltipId,
  isDisabled,
  onClick,
}: IconButtonProps) => (
  <Tooltip
    id={tooltipId}
    tooltip={tooltip}
    placement="left"
    //  Always make the tooltip visible.
    showTooltip
  >
    <span className="inline-block">
      <IconButton
        id={`${tooltipId}-button`}
        icon={icon}
        onClick={onClick}
        isDisabled={isDisabled}
      />
    </span>
  </Tooltip>
));

export default SettingsTableRowActions;
